'use client';
import React, { useEffect } from 'react';
import { Link, usePathname } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/lib/utils';
import Socials from './socials';
import LanguageSwitcher from './language-switcher';
import Image from 'next/image';


interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navigationItems: Array<{ href: string; label: string }>;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, navigationItems }) => {
  const pathname = usePathname();
  const t = useTranslations('nav');
  const tA11y = useTranslations('accessibility');

  const allNavItems = [
    { href: '/' as const, label: t('home') },
    ...navigationItems,
  ];

  

  useEffect(() => {
    const lenis = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).__lenis as { stop: () => void; start: () => void } | undefined : undefined;
    if (isOpen) {
      lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenis?.start();
      document.body.style.overflow = '';
    }
    return () => {
      lenis?.start();
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Safety: always unlock scroll on unmount or route change
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden overflow-hidden">
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-[#0A001F]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            />

            {/* Panel — slides up on exit */}
            <motion.div
              className="absolute inset-0 bg-[#0A001F] overflow-hidden flex flex-col"
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ type: 'tween', duration: 0.55, ease: 'easeInOut' }}
            >
              {/* Background icon */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-full max-h-[500px]"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
              >
                <Image
                  src="/assets/backgrounds/offcanvas-bg.svg"
                  layout="fill"
                  className="w-full h-full object-contain object-[bottom_right] mobile-bg-image"
                  alt=""
                />
              </motion.div>

              {/* Animated glow */}
              <motion.div
                className="absolute top-[-10%] left-[-20%] w-[400px] h-[400px] bg-[radial-gradient(circle,_rgba(135,82,250,0.15)_0%,_transparent_70%)] rounded-full pointer-events-none"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 1 }}
              />

              <div className="flex flex-col h-full relative z-10">
                {/* Header row */}
                <div className="flex items-center justify-between px-[20px] pt-[calc(15px+env(safe-area-inset-top))]">
                  <motion.div
                    initial={{ opacity: 0, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <img
                      src="/assets/brand/logo.svg"
                      alt="Flyingcode logo"
                      aria-hidden="true"
                      width={48}
                      height={48}
                      className="w-[40px] h-auto mix-blend-plus-lighter"
                    />
                  </motion.div>

                  <motion.button
                    onClick={onClose}
                    className="flex flex-col justify-center items-end w-[40px] h-[40px] text-white cursor-pointer -translate-y-[2px]"
                    aria-label={tA11y('closeMenu')}
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="block rounded-full w-[24px] h-[2px] bg-white rotate-45 translate-y-[1px]" />
                    <span className="block rounded-full w-[24px] h-[2px] bg-white -rotate-45 -translate-y-[1px]" />
                  </motion.button>
                </div>

                {/* Nav items */}
                <nav aria-label={tA11y('mobileNavigation')} className="flex-1 flex items-center px-[28px]">
                  <ul className="w-full space-y-[2px]">
                    {allNavItems.map((item, index) => {
                      const isActive = pathname === item.href;
                      return (
                        <motion.li
                          key={item.href}
                          className="overflow-hidden"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.25 + index * 0.07, duration: 0.3 }}
                        >
                          <motion.div
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{
                              delay: 0.25 + index * 0.07,
                              duration: 0.55,
                              ease: [0.22, 1, 0.36, 1],
                            }}
                          >
                            <Link
                              href={item.href}
                              onClick={onClose}
                              className={cn(
                                'group relative flex items-center gap-[12px] py-[14px] text-[38px] font-semibold leading-none transition-all duration-300',
                                isActive
                                  ? 'text-white/20 pointer-events-none'
                                  : 'text-white'
                              )}
                            >
                              {!isActive && (
                                <motion.span
                                  className="absolute bottom-[10px] left-0 h-[1px] bg-theme origin-left"
                                  initial={{ scaleX: 0 }}
                                  whileHover={{ scaleX: 1 }}
                                  transition={{ duration: 0.3, ease: 'easeOut' }}
                                  style={{ width: '100%' }}
                                />
                              )}
                              <motion.span
                                className="relative"
                                whileHover={!isActive ? { x: 6, color: '#8752fa' } : {}}
                                transition={{ duration: 0.2 }}
                              >
                                {item.label}
                              </motion.span>
                            </Link>
                          </motion.div>
                        </motion.li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Footer */}
                <motion.div
                  className="px-[28px] pb-[calc(28px+env(safe-area-inset-bottom))]"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65, duration: 0.4 }}
                >
                  <div className="flex items-end justify-between">
                    <Socials alignment="left" />
                    <LanguageSwitcher direction="up" />
                  </div>
                </motion.div>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileMenu;
